// ============================================
// INVENTORY
// ============================================

import { supabase } from '../../lib/supabase';
import type { InventoryItem, ProtocolComponent } from '../types';
import { buildDrawChangeNote, recomputeComponentsForVial, selectPromotableBackup, type DrawChange } from '../../utils/vialRecompute';
import { requireUserId, throwWithContext, notify } from './_shared';

/* eslint-disable @typescript-eslint/no-explicit-any */

export async function fetchInventory(): Promise<InventoryItem[]> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from('inventory')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throwWithContext("Couldn't load your inventory", error);
  return (data || []).map(mapInventoryFromDb);
}

/**
 * The vial a dose could actually be drawn from RIGHT NOW: active,
 * reconstituted (not sealed in storage), and NOT a combined vial.
 *
 * The old is_active-only lookup (removed 2026-07-08) could return a
 * sealed storage vial
 * (is_researching, bacWater 0) or a mix row (a mix's peptide_id is
 * just its lead peptide), neither of which is the vial the user is
 * injecting this single-peptide dose from. Newest vial wins the tie
 * deterministically (the old .single() with no ORDER BY picked an
 * arbitrary row when two actives existed after a half-failed refill).
 * (Vial lifecycle review 2026-07-08, decisions 4A/9A.)
 */
export async function fetchUsableVialForPeptide(peptideId: string): Promise<InventoryItem | null> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from('inventory')
    .select('*')
    .eq('user_id', userId)
    .eq('peptide_id', peptideId)
    .eq('is_active', true)
    .eq('is_researching', false)
    .is('mix_group_id', null)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  return mapInventoryFromDb(data);
}

export async function fetchInventoryById(id: string): Promise<InventoryItem | null> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from('inventory')
    .select('*')
    .eq('id', id)
    .eq('user_id', userId)
    .single();
  if (error || !data) return null;
  return mapInventoryFromDb(data);
}

export async function fetchActiveInventoryItemsForPeptide(peptideId: string): Promise<InventoryItem[]> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from('inventory')
    .select('*')
    .eq('user_id', userId)
    .eq('peptide_id', peptideId)
    .eq('is_active', true)
    .order('created_at', { ascending: false });
  if (error) return [];
  return (data || []).map(mapInventoryFromDb);
}

/**
 * Stored combo (mix) vials the user could reuse when building a new
 * combined protocol: active, still in storage (is_researching), and a
 * mix. Covers BOTH already-mixed backups (bacWaterMl > 0) and sealed
 * ones (bacWaterMl 0) - the wizard branches on bacWaterMl and, for
 * sealed, collects BAC water before it has a strength. In-use combos
 * (is_researching false) are deliberately excluded: that vial already
 * drives another protocol and reusing it would double-book supply.
 * Newest first; the caller filters to the recipe's peptide set and, on
 * 2+ matches, shows a picker (never a silent wrong-strength pick).
 * (Storage-mix wizard reuse, 2026-07-10.)
 */
export async function fetchStorageMixVials(): Promise<InventoryItem[]> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from('inventory')
    .select('*')
    .eq('user_id', userId)
    .eq('is_active', true)
    .eq('is_researching', true)
    .not('mix_group_id', 'is', null)
    .order('created_at', { ascending: false });
  if (error) return [];
  return (data || []).map(mapInventoryFromDb);
}

export async function addInventoryItem(item: InventoryItem): Promise<void> {
  const userId = await requireUserId();
  const row = mapInventoryToDb(item, userId);
  const { error } = await supabase.from('inventory').insert(row);
  if (error) throwWithContext("Couldn't add your vial to inventory", error);
  notify('inventory');
}

export async function updateInventoryItem(id: string, updates: Partial<InventoryItem>): Promise<void> {
  const userId = await requireUserId();
  const dbUpdates = mapInventoryToDb(updates as InventoryItem);
  delete (dbUpdates as Record<string, unknown>).user_id;
  const { error } = await supabase
    .from('inventory')
    .update(dbUpdates)
    .eq('id', id)
    .eq('user_id', userId);
  if (error) throwWithContext("Couldn't update your inventory item", error);
  notify('inventory');
}

export type AutoSwitchOutcome =
  | { kind: 'no_action' }
  | {
      kind: 'promoted';
      oldVialId: string;
      newVial: InventoryItem;
      /** Set when the promoted vial's mix changed the instructed draw. */
      drawChange: DrawChange | null;
    }
  | { kind: 'multiple_backups'; oldVialId: string; backups: InventoryItem[] }
  /** The only backup(s) are still sealed - route the user to Start Using. */
  | { kind: 'sealed_backup'; oldVialId: string; backup: InventoryItem };

/**
 * Archive a vial AND clear the inventoryId link on every protocol
 * component that points at it, so the next dose log falls back to
 * an actually-active vial instead of dead-ending on the archived row.
 *
 * Closes P0-3 (the "TODO(Step 5)" in DoseLogModal): mark-empty used
 * to archive the vial and leave protocols pointing at it forever -
 * fetchInventoryById returned the archived row (non-null), the
 * active-vial fallback never ran, and no vial ever decremented again.
 *
 * Two sequential writes, deliberately NOT atomic (review decision 8C:
 * accepted risk, caller wraps in try/catch and toasts a retry).
 */
export async function archiveVialAndUnlink(vialId: string): Promise<void> {
  const userId = await requireUserId();

  const { error: archiveErr } = await supabase
    .from('inventory')
    .update({ is_active: false })
    .eq('id', vialId)
    .eq('user_id', userId);
  if (archiveErr) throwWithContext("Couldn't archive that vial", archiveErr);

  const { data: protocolRows, error: protoErr } = await supabase
    .from('protocols')
    .select('id, components')
    .eq('user_id', userId);
  if (protoErr) throwWithContext("Couldn't update the linked protocol", protoErr);

  for (const row of protocolRows ?? []) {
    const components = row.components as Array<{ inventoryId?: string | null }>;
    let touched = false;
    const updated = components.map((c) => {
      if (c.inventoryId === vialId) {
        touched = true;
        return { ...c, inventoryId: null };
      }
      return c;
    });
    if (touched) {
      const { error } = await supabase
        .from('protocols')
        .update({ components: updated })
        .eq('id', row.id)
        .eq('user_id', userId);
      if (error) throwWithContext("Couldn't update the linked protocol", error);
    }
  }

  notify('inventory');
  notify('protocols');
}

/**
 * After a dose log empties a vial, see if we have a backup for the
 * same peptide worth promoting automatically.
 *
 * Policy (reworked 2026-07-08, vial lifecycle review):
 * - Mix vials NEVER auto-switch (9A): a mix's peptide_id is just its
 *   lead peptide, so peptide-keyed matching would pair a combined
 *   vial with a plain single (or vice versa). Emptied mixes fall to
 *   the manual prompt / Refill Combined Vial flow.
 * - Sealed backups (bacWater 0 - added to storage, never mixed) are
 *   NEVER silently promoted (P1-4): promoting one used to put a
 *   0-concentration vial into service with no 28-day clock. They
 *   surface as `sealed_backup` so the caller routes the user into
 *   the existing Start Using flow.
 * - Exactly one reconstituted single backup -> promote: archive old,
 *   flip isResearching, re-link protocols AND recompute each linked
 *   component's concentration/draw units via recomputeComponentsForVial
 *   (2A - a backup mixed differently must update the instructed draw,
 *   not silently keep the old one).
 * - 2+ usable backups -> multiple_backups: never silently pick.
 *
 * Idempotent: if the supplied old-vial id isn't actually empty (or
 * doesn't exist), returns no_action without side effects.
 */
export async function maybeAutoSwitchEmptyVial(emptyVialId: string): Promise<AutoSwitchOutcome> {
  const userId = await requireUserId();
  // Reload the supposedly-empty vial to confirm state. Avoids races
  // where the caller's snapshot is stale.
  const { data: oldRow, error: oldErr } = await supabase
    .from('inventory')
    .select('*')
    .eq('id', emptyVialId)
    .eq('user_id', userId)
    .single();
  if (oldErr || !oldRow) return { kind: 'no_action' };
  const old = mapInventoryFromDb(oldRow);
  if (!old.isActive || old.isResearching) return { kind: 'no_action' };
  if (old.dosesRemaining == null || old.dosesRemaining > 0) return { kind: 'no_action' };
  // Mixes never auto-switch (9A).
  if (old.mixGroupId || (old.mixRecipe?.length ?? 0) > 0) return { kind: 'no_action' };

  // Find unstarted backups of the same peptide (singles only - a mix
  // row sharing the lead peptide_id is not a backup for this vial).
  const { data: backupRows } = await supabase
    .from('inventory')
    .select('*')
    .eq('user_id', userId)
    .eq('peptide_id', old.peptideId)
    .eq('is_active', true)
    .eq('is_researching', true)
    .is('mix_group_id', null)
    .order('created_at', { ascending: true });

  // A parked vial reserved by a SCHEDULED protocol is not a backup - it
  // looks identical to one (researching, real water, null counts) but
  // promoting it would double-book supply: the activation engine stamps
  // that vial with the scheduled protocol's counts on its start day while
  // this protocol is already drawing it down (scheduled-protocols guard,
  // adversarial review A1). Direct table read - importing the activation
  // helper here would cycle data/cloud <-> utils.
  const { data: scheduledRows } = await supabase
    .from('protocols')
    .select('components')
    .eq('user_id', userId)
    .eq('status', 'scheduled');
  const reservedVialIds = new Set<string>();
  for (const row of scheduledRows ?? []) {
    for (const c of (row.components as ProtocolComponent[]) ?? []) {
      if (c.inventoryId) reservedVialIds.add(c.inventoryId);
    }
  }

  const backups = (backupRows || [])
    .map(mapInventoryFromDb)
    .filter((b) => !reservedVialIds.has(b.id));
  const decision = selectPromotableBackup(backups);
  if (decision.kind === 'none') return { kind: 'no_action' };
  if (decision.kind === 'sealed') {
    return { kind: 'sealed_backup', oldVialId: old.id, backup: decision.vial! };
  }
  if (decision.kind === 'multiple') {
    return { kind: 'multiple_backups', oldVialId: old.id, backups: decision.candidates! };
  }

  // Exactly one reconstituted backup - auto-promote.
  const promoted = decision.vial!;

  // Archive the old vial.
  await supabase
    .from('inventory')
    .update({ is_active: false })
    .eq('id', old.id)
    .eq('user_id', userId);

  // Promote the backup.
  await supabase
    .from('inventory')
    .update({ is_researching: false })
    .eq('id', promoted.id)
    .eq('user_id', userId);

  // Re-link any protocols that referenced the old vial, recomputing
  // each relinked component's vial-derived fields so the instructed
  // draw matches the vial now in use (2A). We do this client-side
  // instead of via RPC so the existing protocol-update hooks (cache
  // invalidation, drift detection) still fire.
  const { data: protocolRows } = await supabase
    .from('protocols')
    .select('id, components')
    .eq('user_id', userId);

  let drawChange: DrawChange | null = null;
  // Protocols whose instructed draw actually moved on the promote - each
  // gets its stale AI summary refreshed below (the summary quotes units,
  // so a concentration change makes it wrong until regenerated).
  const drawChangedProtocols: Array<{ id: string; change: DrawChange }> = [];
  for (const row of protocolRows ?? []) {
    const components = row.components as ProtocolComponent[];
    const matched = components.filter((c) => c.inventoryId === old.id);
    if (matched.length === 0) continue;

    // Recompute concentration/draw for the components moving to the
    // promoted vial. A null result means the promoted vial can't
    // support the math (shouldn't happen - promote requires bacWater
    // > 0 - but refusing beats persisting a 0 concentration): fall
    // back to a pointer-only relink, matching the old behavior.
    const recompute = recomputeComponentsForVial(matched, { ...promoted, isResearching: false });
    const byId = new Map(
      (recompute?.components ?? matched).map((c) => [c.id, c]),
    );
    if (recompute && recompute.drawChanges.length > 0) {
      if (!drawChange) drawChange = recompute.drawChanges[0];
      drawChangedProtocols.push({ id: row.id, change: recompute.drawChanges[0] });
    }

    const updated = components.map((c) => {
      if (c.inventoryId !== old.id) return c;
      const next = byId.get(c.id) ?? c;
      return { ...next, inventoryId: promoted.id };
    });
    await supabase
      .from('protocols')
      .update({ components: updated })
      .eq('id', row.id)
      .eq('user_id', userId);
  }

  notify('inventory');
  notify('protocols');

  // Refresh the AI summary for each protocol whose draw moved. Dynamic
  // import avoids a static cycle (protocols.ts imports this module).
  // Fire-and-forget: the summary regenerates in the background.
  if (drawChangedProtocols.length > 0) {
    import('./protocols')
      .then(({ refreshProtocolSummary }) => {
        for (const p of drawChangedProtocols) {
          refreshProtocolSummary(p.id, buildDrawChangeNote(p.change.oldUnits, p.change.newUnits));
        }
      })
      .catch((err) => console.error('[cloud] promote summary refresh failed:', err));
  }
  return {
    kind: 'promoted',
    oldVialId: old.id,
    newVial: { ...promoted, isResearching: false },
    drawChange,
  };
}

/**
 * Atomic refill: insert the new vial + archive the old + repoint the
 * provided protocols' components, in ONE transaction via the
 * refill_vial RPC (migration 20260708_refill_vial_rpc.sql). Replaces
 * the old two-write replaceVialAndRelink flow, whose half-failure
 * left two active vials and a protocol pointing at the old one
 * (P1-5). All derived values arrive pre-computed from
 * buildRefillPlan (decision 1A / C7); the RPC validates identity
 * (same peptide, same mix-ness, only old->new repoints - 10A) and
 * writes.
 */
export async function refillVial(
  oldVialId: string,
  newVial: InventoryItem,
  updates: Array<{ protocolId: string; components: unknown[] }>,
): Promise<void> {
  await requireUserId();
  const row = mapInventoryToDb(newVial);
  delete (row as Record<string, unknown>).user_id; // RPC stamps auth.uid()
  const { error } = await supabase.rpc('refill_vial', {
    p_old_vial_id: oldVialId,
    p_new_vial: row,
    p_component_updates: updates.map((u) => ({
      protocol_id: u.protocolId,
      components: u.components,
    })),
  });
  if (error) throwWithContext("Couldn't refill that vial", error);
  notify('inventory');
  notify('protocols');
}

function mapInventoryFromDb(row: any): InventoryItem {
  // total_doses + doses_remaining are nullable since 2026-04-29 (vials
  // added without a planned dose). Number(null) → 0 would silently lie
  // about empty vials, so we preserve null explicitly.
  const totalDoses = row.total_doses == null ? null : Number(row.total_doses);
  const dosesRemaining = row.doses_remaining == null ? null : Number(row.doses_remaining);
  return {
    id: row.id,
    peptideId: row.peptide_id,
    peptideName: row.peptide_name,
    vialSizeMg: Number(row.vial_size_mg),
    bacWaterMl: Number(row.bac_water_ml),
    concentrationMcgPerUnit: Number(row.concentration_mcg_per_unit),
    dateReconstituted: row.date_reconstituted,
    expirationDate: row.expiration_date,
    totalDoses,
    dosesUsed: Number(row.doses_used),
    dosesRemaining,
    isActive: row.is_active,
    isResearching: row.is_researching ?? false,
    notes: row.notes ?? undefined,
    mixGroupId: row.mix_group_id ?? null,
    mixDisplayName: row.mix_display_name ?? null,
    mixRecipe: row.mix_recipe ?? null,
    createdAt: row.created_at,
  };
}

function mapInventoryToDb(item: Partial<InventoryItem>, userId?: string): Record<string, unknown> {
  const m: Record<string, unknown> = {};
  if (userId) m.user_id = userId;
  if (item.id !== undefined) m.id = item.id;
  if (item.peptideId !== undefined) m.peptide_id = item.peptideId;
  if (item.peptideName !== undefined) m.peptide_name = item.peptideName;
  if (item.vialSizeMg !== undefined) m.vial_size_mg = item.vialSizeMg;
  if (item.bacWaterMl !== undefined) m.bac_water_ml = item.bacWaterMl;
  if (item.concentrationMcgPerUnit !== undefined) m.concentration_mcg_per_unit = item.concentrationMcgPerUnit;
  if (item.dateReconstituted !== undefined) m.date_reconstituted = item.dateReconstituted;
  if (item.expirationDate !== undefined) m.expiration_date = item.expirationDate;
  if (item.totalDoses !== undefined) m.total_doses = item.totalDoses;
  if (item.dosesUsed !== undefined) m.doses_used = item.dosesUsed;
  if (item.dosesRemaining !== undefined) m.doses_remaining = item.dosesRemaining;
  if (item.isActive !== undefined) m.is_active = item.isActive;
  if (item.isResearching !== undefined) m.is_researching = item.isResearching;
  if (item.notes !== undefined) m.notes = item.notes;
  if (item.mixGroupId !== undefined) m.mix_group_id = item.mixGroupId;
  if (item.mixDisplayName !== undefined) m.mix_display_name = item.mixDisplayName;
  if (item.mixRecipe !== undefined) m.mix_recipe = item.mixRecipe;
  if (item.createdAt !== undefined) m.created_at = item.createdAt;
  return m;
}
